<template>
  <div class="relative">
    <MultiSelectSearch
      :search-groups="searchGroups"
      :selected-items="selectedItemsForSearch"
      :placeholder="t('placeholder')"
      :input-id="'analytics-search'"
      :min-search-length="0"
      :debounce-delay="0"
      :show-component="true"
      :initial-selections="initialSelections"
      @update-selections="handleUpdateSelections"
      @search="handleSearch"
      @toggle-item="handleToggleItem"
      @clear-results="handleClearResults"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { useI18n } from "vue-i18n";
import { MultiSelectSearch } from "@estaly/ui";

export default {
  name: "AnalyticsSelector",
  components: {
    MultiSelectSearch,
  },
  emits: ["update-selections"],

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      selectedOrganizations: [],
      selectedStores: [],
      organizationsResults: [],
      storesResults: [],
    };
  },

  computed: {
    ...mapState("businessVerticals", { businessVerticals: "index" }),

    organizations() {
      const seen = new Map();
      this.businessVerticals.forEach((businessVertical) => {
        const organization = businessVertical.store?.organization;
        if (organization && !seen.has(organization.id)) {
          seen.set(organization.id, { id: organization.id, name: organization.name });
        }
      });
      return Array.from(seen.values());
    },

    stores() {
      const seen = new Map();
      this.businessVerticals.forEach((businessVertical) => {
        const store = businessVertical.store;
        if (store && !seen.has(store.id)) {
          seen.set(store.id, { id: store.id, name: store.salesChannel });
        }
      });
      return Array.from(seen.values());
    },

    searchGroups() {
      return [
        {
          key: "organizations",
          label: this.t("organizations_label"),
          items: this.organizationsResults,
          getDisplayName: (organization) => organization.name,
          isSelected: (organizationId) =>
            this.selectedOrganizations.some((organization) => organization.id === organizationId),
        },
        {
          key: "stores",
          label: this.t("stores_label"),
          items: this.storesResults,
          getDisplayName: (store) => store.name,
          isSelected: (storeId) => this.selectedStores.some((store) => store.id === storeId),
        },
      ];
    },

    initialSelections() {
      return {
        organizations: this.selectedOrganizations,
        stores: this.selectedStores,
      };
    },

    selectedItemsForSearch() {
      const organizations = this.selectedOrganizations.map((organization) => ({
        id: organization.id,
        label: organization.name,
        groupKey: "organizations",
      }));
      const stores = this.selectedStores.map((store) => ({
        id: store.id,
        label: store.name,
        groupKey: "stores",
      }));
      return [...organizations, ...stores];
    },
  },

  created() {
    this.fetchBusinessVerticals();
  },

  mounted() {
    this.organizationsResults = [...this.organizations];
    this.storesResults = [...this.stores];
  },

  watch: {
    organizations(newValue) {
      this.organizationsResults = [...newValue];
    },
    stores(newValue) {
      this.storesResults = [...newValue];
    },
  },

  methods: {
    ...mapActions("businessVerticals", { fetchBusinessVerticals: "fetchIndex" }),

    handleSearch(keywords) {
      if (!keywords || keywords.trim() === "") {
        this.organizationsResults = [...this.organizations];
        this.storesResults = [...this.stores];
        return;
      }

      const lowerKeywords = keywords.toLowerCase();
      this.organizationsResults = this.organizations.filter((organization) =>
        organization.name.toLowerCase().includes(lowerKeywords),
      );
      this.storesResults = this.stores.filter((store) => store.name.toLowerCase().includes(lowerKeywords));
    },

    handleToggleItem({ item, groupKey }) {
      if (groupKey === "organizations") {
        const index = this.selectedOrganizations.findIndex((organization) => organization.id === item.id);
        if (index >= 0) {
          this.selectedOrganizations.splice(index, 1);
        } else {
          this.selectedOrganizations = [item];
          this.selectedStores = [];
        }
      } else if (groupKey === "stores") {
        const index = this.selectedStores.findIndex((store) => store.id === item.id);
        if (index >= 0) {
          this.selectedStores.splice(index, 1);
        } else {
          this.selectedStores.push(item);
          this.selectedOrganizations = [];
        }
      }
      this.emitSelections();
    },

    handleUpdateSelections(selections) {
      this.selectedOrganizations = selections.organizations || [];
      this.selectedStores = selections.stores || [];
      this.emitSelections();
    },

    handleClearResults() {
      this.organizationsResults = [...this.organizations];
      this.storesResults = [...this.stores];
    },

    emitSelections() {
      this.$emit("update-selections", {
        organizations: this.selectedOrganizations,
        stores: this.selectedStores,
      });
    },
  },
};
</script>

<i18n src="./AnalyticsSelector.i18n.json"></i18n>
